import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import GoogleMap from '@/components/ui/GoogleMap';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import type { Coordinates, MarkerFormData, ReportFormData } from '@/types';
import api from '@/services/api';

const ReportPage: React.FC = () => {
  const [selectedPosition, setSelectedPosition] = useState<Coordinates | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState<MarkerFormData>({
    coordinates: { latitude: 0, longitude: 0 },
    streetName: '',
    streetNameCorrection: '',
    currentLine: {
      operator: '',
      technology: 'Unknown',
      maxSpeed: {
        download: 0,
        upload: 0,
        unit: 'Mbps'
      },
      isActivated: false
    }
  });

  const [reportData, setReportData] = useState<ReportFormData>({
    issue: 'no_fiber',
    description: '',
    priority: 'medium',
    reportedBy: {
      name: '',
      username: '',
      email: '',
      phone: ''
    }
  });

  const handleMapClick = useCallback((coordinates: Coordinates) => {
    setSelectedPosition(coordinates);
    setFormData(prev => ({
      ...prev,
      coordinates
    }));
  }, []);

  const handleAddressSelect = useCallback((address: string, coordinates: Coordinates) => {
    setSelectedPosition(coordinates);
    setFormData(prev => ({
      ...prev,
      coordinates,
      streetName: address
    }));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedPosition) {
      toast.error('Seleziona una posizione sulla mappa');
      return;
    }

    if (!formData.streetName.trim()) {
      toast.error('Inserisci il nome della via');
      return;
    }

    setIsLoading(true);

    try {
      // First create the marker
      const markerPayload = {
        coordinates: selectedPosition,
        streetName: formData.streetName,
        streetNameCorrection: formData.streetNameCorrection,
        currentLine: formData.currentLine
      };

      const markerResponse = await api.post('/markers/create', markerPayload);

      if (markerResponse.data.success) {
        // Then add the report to the created marker
        const markerId = markerResponse.data.data._id;
        const reportResponse = await api.post(`/markers/${markerId}/reports`, reportData);

        if (reportResponse.data.success) {
          toast.success('Segnalazione inviata con successo!');
          navigate('/');
        } else {
          toast.error('Errore nell\'aggiunta del report');
        }
      } else {
        toast.error('Errore nella creazione del marker');
      }
    } catch (error: unknown) {
      console.error('Report submission error:', error);
      const message = (error as any).response?.data?.message || 'Errore nell\'invio della segnalazione';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Segnala un Problema
          </h1>
          <p className="text-gray-600">
            Seleziona la posizione sulla mappa e descrivi il problema con la connessione
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Map Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">1. Seleziona la Posizione</h2>
            <p className="text-gray-600 mb-4">
              Usa la barra di ricerca sulla mappa per trovare un indirizzo, oppure clicca direttamente sulla mappa per indicare dove si trova il problema.
            </p>

            <GoogleMap
              center={{ latitude: 41.9028, longitude: 12.4964 }}
              zoom={10}
              height="400px"
              showSearchBox={true}
              onMapClick={handleMapClick}
              onAddressSelect={handleAddressSelect}
              markers={selectedPosition ? [{
                position: selectedPosition,
                title: 'Posizione selezionata',
                info: 'Problema segnalato qui'
              }] : []}
            />

            {selectedPosition && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                <p className="text-green-800 text-sm">
                  ✓ Posizione selezionata: {selectedPosition.latitude.toFixed(6)}, {selectedPosition.longitude.toFixed(6)}
                </p>
              </div>
            )}
          </div>

          {/* Location Details */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">2. Dettagli della Posizione</h2>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome della Via *
                </label>
                <input
                  type="text"
                  value={formData.streetName}
                  onChange={(e) => setFormData(prev => ({ ...prev, streetName: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="es. Via Roma, 123"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Correzione Nome Via
                </label>
                <input
                  type="text"
                  value={formData.streetNameCorrection}
                  onChange={(e) => setFormData(prev => ({ ...prev, streetNameCorrection: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Se diverso da Google Maps"
                />
              </div>
            </div>
          </div>

          {/* Current Connection */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">3. Connessione Attuale</h2>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Operatore
                </label>
                <input
                  type="text"
                  value={formData.currentLine?.operator || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    currentLine: { ...prev.currentLine!, operator: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="es. TIM, Vodafone, Fastweb..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tecnologia
                </label>
                <select
                  value={formData.currentLine?.technology || 'Unknown'}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    currentLine: { ...prev.currentLine!, technology: e.target.value as any }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Unknown">Non so</option>
                  <option value="None">Nessuna connessione</option>
                  <option value="ADSL">ADSL</option>
                  <option value="VDSL">VDSL</option>
                  <option value="FTTC">FTTC (Fibra fino al cabinet)</option>
                  <option value="FTTH">FTTH (Fibra fino a casa)</option>
                  <option value="FWA">FWA (Fixed Wireless)</option>
                  <option value="Satellite">Satellite</option>
                  <option value="Mobile">Mobile/4G/5G</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Velocità Download (Mbps)
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.currentLine?.maxSpeed?.download || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    currentLine: {
                      ...prev.currentLine!,
                      maxSpeed: {
                        ...prev.currentLine!.maxSpeed!,
                        download: parseInt(e.target.value) || 0
                      }
                    }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="es. 20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Velocità Upload (Mbps)
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.currentLine?.maxSpeed?.upload || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    currentLine: {
                      ...prev.currentLine!,
                      maxSpeed: {
                        ...prev.currentLine!.maxSpeed!,
                        upload: parseInt(e.target.value) || 0
                      }
                    }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="es. 5"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.currentLine?.isActivated || false}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    currentLine: { ...prev.currentLine!, isActivated: e.target.checked }
                  }))}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">La linea è attualmente attiva</span>
              </label>
            </div>
          </div>

          {/* Problem Report */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">4. Descrizione del Problema</h2>

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo di Problema
                </label>
                <select
                  value={reportData.issue}
                  onChange={(e) => setReportData(prev => ({ ...prev, issue: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="no_fiber">Fibra ottica non disponibile</option>
                  <option value="slow_speed">Velocità troppo bassa</option>
                  <option value="connection_issues">Problemi di connessione</option>
                  <option value="service_unavailable">Servizio non disponibile</option>
                  <option value="other">Altro</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priorità
                </label>
                <select
                  value={reportData.priority}
                  onChange={(e) => setReportData(prev => ({ ...prev, priority: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="low">Bassa</option>
                  <option value="medium">Media</option>
                  <option value="high">Alta</option>
                  <option value="urgent">Urgente</option>
                </select>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descrizione Dettagliata
              </label>
              <textarea
                value={reportData.description}
                onChange={(e) => setReportData(prev => ({ ...prev, description: e.target.value }))}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Descrivi il problema in dettaglio..."
              />
            </div>

            {/* Contact Info */}
            {/*     <h3 className="text-lg font-medium mb-3">Informazioni di Contatto (Opzionale)</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome
                </label>
                <input
                  type="text"
                  value={reportData.reportedBy?.name || ''}
                  onChange={(e) => setReportData(prev => ({
                    ...prev,
                    reportedBy: { ...prev.reportedBy!, name: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Il tuo nome"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={reportData.reportedBy?.email || ''}
                  onChange={(e) => setReportData(prev => ({
                    ...prev,
                    reportedBy: { ...prev.reportedBy!, email: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="la.tua@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefono
                </label>
                <input
                  type="tel"
                  value={reportData.reportedBy?.phone || ''}
                  onChange={(e) => setReportData(prev => ({
                    ...prev,
                    reportedBy: { ...prev.reportedBy!, phone: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Il tuo numero"
                />
              </div>
            </div> */}
          </div>

          {/* Submit Button */}
          <div className="flex justify-center space-x-4">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Annulla
            </button>

            <button
              type="submit"
              disabled={isLoading || !selectedPosition}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium transition-colors flex items-center"
            >
              {isLoading ? (
                <LoadingSpinner size="sm" className="mr-2" />
              ) : null}
              {isLoading ? 'Inviando...' : 'Invia Segnalazione'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReportPage;